import type { Infer } from 'superstruct';
import {
	any,
	array,
	assign,
	boolean,
	nullable,
	number,
	object,
	optional,
	string,
} from 'superstruct';

import {
	ExportTypeDto,
	ListResourceDto,
	PaginationQuery,
} from '@adflow/shared/util-data';
import { adflowApiSchema } from '@adflow/shared/util-route-schema';
import {
	parsableBoolean,
	parsableInteger,
} from '@adflow/shared/util-superstruct';

import { ViewFacebookAdAccountDto } from './facebook-ad-account.dto';
import { FacebookSavedAudience } from './facebook-ad-account-saved-audience.dto';
import { FacebookCustomAudienceItemDto } from './facebook-custom-audience.dto';
import {
	FacebookAdAccountPageOptionDto,
	ViewFacebookPageDto,
} from './facebook-page.dto';
import {
	AddFacebookTemplateDto,
	ViewFacebookTemplateDto,
} from './facebook-template.dto';
import { ListFacebookPagesSort } from './list-facebook-template-query.dto';

const p = adflowApiSchema.prefix('facebook');

export const AddFacebookTemplate = p.post('', {
	req: object({
		body: AddFacebookTemplateDto,
		query: object({ duplicated: optional(parsableBoolean()) }),
	}),
	res: ViewFacebookTemplateDto,
});

export const UploadFacebookTemplate = p.post('templates/:templateId/upload', {
	req: object({
		params: object({ templateId: parsableInteger() }),
	}),
	res: any(),
});

export const GetFacebookTemplateUploadProgress = p.get(
	'templates/:templateId/upload-progress',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
		}),
		res: nullable(
			object({
				complete: number(),
				total: number(),
			})
		),
	}
);

export const ArchiveFacebookTemplate = p.post('templates/:templateId/archive', {
	req: object({
		params: object({ templateId: parsableInteger() }),
	}),
	res: ViewFacebookTemplateDto,
});

export const DeleteFacebookTemplate = p.post('templates/:templateId/delete', {
	req: object({
		params: object({ templateId: parsableInteger() }),
	}),
	res: object({}),
});

export const DeleteFacebookTemplates = p.delete('templates/delete', {
	req: object({
		body: object({ templateIds: array(parsableInteger()) }),
	}),
	res: object({}),
});

export const ListFacebookPagesQuery = assign(
	PaginationQuery,
	ListFacebookPagesSort,
	object({ search: optional(string()) })
);

export type ListFacebookPagesQuery = Infer<typeof ListFacebookPagesQuery>;

export const ListFacebookPages = p.get('pages', {
	req: object({
		query: ListFacebookPagesQuery,
	}),
	res: ListResourceDto(ViewFacebookPageDto),
});

export const GetFacebookTemplateAdAccounts = p.get('ad_accounts', {
	req: object({}),
	res: array(ViewFacebookAdAccountDto),
});

export const GetFacebookTemplateAdAccountSavedAudiences = p.get(
	'ad_accounts/:avbId/saved_audiences',
	{
		req: object({
			params: object({ avbId: string() }),
		}),
		res: array(FacebookSavedAudience),
	}
);

export const GetFacebookTemplateAdAccountPages = p.get(
	'ad_accounts/:avbId/pages',
	{
		req: object({
			params: object({ avbId: string() }),
			query: object({ search: optional(string()) }),
		}),
		res: array(FacebookAdAccountPageOptionDto),
	}
);

export const GetFacebookTemplateAdAccountCustomAudiences = p.get(
	'ad_accounts/:avbId/custom_audiences',
	{
		req: object({
			params: object({ avbId: string() }),
		}),
		res: array(FacebookCustomAudienceItemDto),
	}
);

export const FacebookProductCatalogDto = object({
	id: string(),
	name: optional(string()),
	product_count: optional(number()),
});
export type FacebookProductCatalogDto = Infer<typeof FacebookProductCatalogDto>;

export const FacebookProductSetDto = object({
	id: string(),
	name: optional(string()),
});
export type FacebookProductSetDto = Infer<typeof FacebookProductSetDto>;

export const GetFacebookTemplateProductCatalogs = p.get('product_catalogs', {
	req: object({
		query: object({ avbIds: optional(array(string())) }),
	}),
	res: array(FacebookProductCatalogDto),
});

export const GetFacebookTemplateProductSets = p.get('product_sets', {
	req: object({
		query: object({ avbId: string(), productCatalogId: string() }),
	}),
	res: array(FacebookProductSetDto),
});

export const GetFacebookTemplateEnums = p.get('enums', {
	req: object({}),
	res: object({
		statuses: array(string()),
		campaignSpecialAdCategories: array(string()),
		campaignObjectives: array(string()),
		adSetOptimizationGoals: array(string()),
		adSetBillingEvents: array(string()),
		adSetBidStrategies: array(string()),
		adCreativeCallToActionTypes: array(string()),
	}),
});

export const FacebookAdInterestResponse = object({
	id: string(),
	name: string(),
	audience_size_lower_bound: number(),
	audience_size_upper_bound: number(),
	path: array(string()),
	description: optional(nullable(string())),
	topic: optional(string()),
});

export const GetFacebookTemplateAdInterests = p.get('ad_interests', {
	req: object({
		query: object({ search: optional(string()) }),
	}),
	res: array(FacebookAdInterestResponse),
});

export const FacebookZipResponse = object({
	key: string(),
	name: string(),
	type: string(),
	country_code: string(),
	country_name: string(),
	region: optional(string()),
	region_id: optional(number()),
	primary_city: optional(string()),
	primary_city_id: optional(number()),
	supports_city: boolean(),
});

export const GetFacebookTemplateZips = p.get('zips', {
	req: object({
		query: object({ search: optional(string()) }),
	}),
	res: array(FacebookZipResponse),
});

export const FacebookRegionResponse = object({
	key: string(),
	name: string(),
	type: string(),
	country_code: string(),
	country_name: string(),
	supports_region: boolean(),
	supports_city: boolean(),
});

export const GetFacebookTemplateRegions = p.get('regions', {
	req: object({
		query: object({ search: optional(string()) }),
	}),
	res: array(FacebookRegionResponse),
});

export const FacebookCityResponse = object({
	key: string(),
	name: string(),
	type: string(),
	country_code: string(),
	country_name: string(),
	region: optional(string()),
	region_id: optional(number()),
	supports_region: boolean(),
	supports_city: boolean(),
});

export const GetFacebookTemplateCities = p.get('cities', {
	req: object({
		query: object({ search: optional(string()) }),
	}),
	res: array(FacebookCityResponse),
});

export const FacebookCountryResponse = object({
	key: string(),
	name: string(),
	type: string(),
	country_code: string(),
	country_name: string(),
	supports_region: boolean(),
	supports_city: boolean(),
});

export const GetFacebookTemplateCountries = p.get('countries', {
	req: object({
		query: object({ search: optional(string()) }),
	}),
	res: array(FacebookCountryResponse),
});

export const FacebookBehaviorResponse = object({
	id: string(),
	name: string(),
	type: string(),
	path: array(string()),
	description: string(),
	real_time_cluster: boolean(),
	audience_size_lower_bound: number(),
	audience_size_upper_bound: number(),
});

export const GetFacebookTemplateBehaviors = p.get('behaviors', {
	req: object({}),
	res: array(FacebookBehaviorResponse),
});

export const CreateFacebookPagesExport = p.post('exports', {
	req: object({
		body: object({
			filter: ListFacebookPagesQuery,
			type: ExportTypeDto,
			columns: array(string()),
		}),
	}),
	res: object({}),
});

export const GetFacebookPagesExport = p.get('exports/:fileName', {
	req: object({
		params: object({ fileName: string() }),
	}),
	res: object({}),
});

export const SyncFacebookAccounts = p.post('sync-accounts', {
	req: object({}),
	res: object({}),
});
